import { Box, Button, LinearProgress, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { StarGoal } from '@/api/types';
import { useChildTokens } from './useChildTokens';

/**
 * Stjärnmåls-kort: belöning + progress (currentStars/targetStars).
 *
 * Föräldern skapar målen, barnet väljer vilket som ska räknas upp. Därför två olika
 * handlingar på samma kort, aldrig samtidigt för samma person:
 *  - `canSelect` (barnet): väljer att stjärnorna ska gå till DET HÄR målet. Det valda
 *    målet får en tydligt markerad ram och en "samlar hit"-rad i stället för knappen —
 *    valet ska gå att se på en halv sekund utan att läsa, och det ska inte finnas en
 *    knapp att trycka på för något som redan gäller.
 *  - `canFulfill` (förälder/handledare): arkiverar ett uppnått mål när belöningen getts.
 */
type StarGoalCardProps = {
  goal: StarGoal;
  /** Visar uppfyll-knappen (endast förälder/handledare). */
  canFulfill?: boolean;
  onFulfill?: (goalId: string) => void;
  /** Visar väljaren (barnet/tonåringen väljer vart stjärnorna går). */
  canSelect?: boolean;
  onSelect?: (goalId: string) => void;
};

const StarGoalCard = ({
  goal,
  canFulfill = false,
  onFulfill,
  canSelect = false,
  onSelect,
}: StarGoalCardProps) => {
  const { t } = useTranslation();
  const tokens = useChildTokens();

  const target = Math.max(goal.targetStars, 1);
  const current = Math.min(goal.currentStars, target);
  const percent = Math.round((current / target) * 100);
  const reached = goal.currentStars >= goal.targetStars;
  // Väljaren visas bara för mål som fortfarande kan samla stjärnor. Ett uppnått mål
  // väntar på en vuxen, inte på ett nytt val.
  const selectable = canSelect && !!onSelect && goal.status === 'active' && !reached;

  return (
    <Box
      sx={{
        bgcolor: tokens.card,
        border: goal.selected ? '2px solid' : '1px solid',
        borderColor: goal.selected ? 'warning.main' : tokens.border,
        borderRadius: '16px',
        p: 2,
        boxShadow: tokens.shadow,
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Box aria-hidden sx={{ fontSize: '1.6rem' }}>
          {goal.rewardEmoji ?? '🎁'}
        </Box>
        <Typography sx={{ fontWeight: 800, fontSize: '1rem', color: tokens.text, flex: 1 }} noWrap>
          {goal.rewardName}
        </Typography>
        <Typography sx={{ fontWeight: 800, fontSize: '0.9rem', color: 'warning.main' }}>
          ⭐ {goal.currentStars}/{goal.targetStars}
        </Typography>
      </Box>
      <LinearProgress
        variant="determinate"
        value={percent}
        aria-label={
          t('child.stars.goalProgressAria', {
            current: goal.currentStars,
            target: goal.targetStars,
          }) as string
        }
        sx={{
          height: 10,
          borderRadius: 5,
          bgcolor: tokens.amberLight,
          '& .MuiLinearProgress-bar': { bgcolor: 'warning.main', borderRadius: 5 },
        }}
      />
      {reached && (
        <Typography sx={{ fontSize: '0.85rem', fontWeight: 700, color: 'success.main' }}>
          {t('child.stars.goalReached')}
        </Typography>
      )}
      {goal.selected && !reached && (
        <Typography sx={{ fontSize: '0.85rem', fontWeight: 700, color: 'warning.main' }}>
          {t('child.stars.goalCollectingHere')}
        </Typography>
      )}
      {selectable && !goal.selected && (
        <Button
          variant="outlined"
          color="warning"
          fullWidth
          onClick={() => onSelect(goal.id)}
          aria-label={t('child.stars.goalSelectAria', { reward: goal.rewardName }) as string}
          sx={{ textTransform: 'none', fontWeight: 700, borderRadius: '12px', mt: 0.5 }}
        >
          {t('child.stars.goalSelect')}
        </Button>
      )}
      {canFulfill && reached && onFulfill && (
        <Button
          variant="contained"
          color="success"
          onClick={() => onFulfill(goal.id)}
          sx={{ alignSelf: 'flex-start', mt: 0.5 }}
        >
          {t('child.stars.goalFulfill')}
        </Button>
      )}
    </Box>
  );
};

export default StarGoalCard;
